/**
 * The collision itself: one impulse through the contact point that moves both
 * boats. No dice, no table of outcomes — the same two poses and velocities always
 * produce the same result, which is what replay depends on.
 *
 * `BoatState` carries no angular velocity, and adding one would reach every
 * caller of a three-field type. So the angular impulse is integrated over the
 * part of the turn that is left into a finite heading change. Same physics; the
 * spin simply does not outlive the turn it happened in.
 */
import { hullReach, type HullCapsule, type HullDimensions } from './boatHull.ts';
import { BOAT_BOUNCE_KEEP, BOAT_SKIN_PX } from './constants.ts';
import { CRASH_FRICTION, CRASH_HARSHNESS } from '../../constants.ts';
import { restitutionAt } from '../../crash.ts';
import { add, clamp, dot, lengthSq, scale, sub } from '../../math.ts';
import type { Vec2 } from '../../types.ts';

const EPSILON = 1e-9;

export type ImpactBody = {
  capsule: HullCapsule;
  /** World velocity, in path pixels per turn — the same unit as `BoatState.speed`. */
  velocity: Vec2;
  /** Relative only; the solver uses ratios. */
  mass: number;
  /** Yaw inertia about the hull centre, in mass times pixels squared. */
  inertia: number;
};

export type ImpactChange = {
  /** Velocity after the hit. */
  velocity: Vec2;
  /** Radians the hull turns before the turn runs out. */
  yaw: number;
};

export type BoatImpact = {
  a: ImpactChange;
  b: ImpactChange;
  /** Normal impulse magnitude. The damage pass reads this; nothing here spends it. */
  impulse: number;
  /** Closing speed along the normal at the moment of contact. Always positive. */
  closingSpeed: number;
};

/**
 * Mass and yaw inertia from the two numbers a boat already stores. Deck area
 * stands in for displacement, and the inertia is the solid-ellipse figure, so a
 * long hull resists a spin more than a stubby one of the same area.
 */
export function impactBody(
  dimensions: HullDimensions,
  capsule: HullCapsule,
  velocity: Vec2,
): ImpactBody {
  const mass = Math.max(dimensions.lengthM * dimensions.beamM, EPSILON);
  const semiLength = hullReach(capsule);
  const semiBeam = capsule.radius;

  return {
    capsule,
    velocity,
    mass,
    inertia: Math.max((mass * (semiLength * semiLength + semiBeam * semiBeam)) / 4, EPSILON),
  };
}

/**
 * Resolve one contact. `normal` points from `a` toward `b`, the direction
 * `hullContact` reports. `remainingTurn` is how much of the turn is left when
 * the hulls meet, which is what the angular impulse gets to rotate through.
 *
 * Returns null when the hulls are already separating at the contact: a boat that
 * is pulling away must not be hit a second time by its own overlap.
 */
export function resolveBoatImpact(
  a: ImpactBody,
  b: ImpactBody,
  point: Vec2,
  normal: Vec2,
  remainingTurn: number,
  harshness: number = CRASH_HARSHNESS,
): BoatImpact | null {
  const leverA = sub(point, a.capsule.center);
  const leverB = sub(point, b.capsule.center);
  const relative = sub(b.velocity, a.velocity);
  const closing = dot(relative, normal);

  if (closing >= -EPSILON) {
    return null;
  }

  const normalShare = shareAlong(a, b, leverA, leverB, normal);

  if (normalShare < EPSILON) {
    return null;
  }

  const impulse = (-(1 + restitution(closing, harshness)) * closing) / normalShare;
  const tangent = { x: -normal.y, y: normal.x };
  const slide = dot(relative, tangent);
  const tangentShare = shareAlong(a, b, leverA, leverB, tangent);
  // Coulomb: the scrape along the hulls can never out-pull the impulse that made it.
  const friction =
    tangentShare < EPSILON
      ? 0
      : clampMagnitude(-slide / tangentShare, CRASH_FRICTION * impulse);
  const applied = add(scale(normal, impulse), scale(tangent, friction));
  const span = Math.max(0, Math.min(1, remainingTurn));

  return {
    a: change(a, leverA, scale(applied, -1), span),
    b: change(b, leverB, applied, span),
    impulse,
    closingSpeed: -closing,
  };
}

export type PartedHull = ImpactChange & {
  center: Vec2;
};

/**
 * Put two hulls that have just hit back on the outside of each other.
 *
 * The impulse alone will spend an off-centre blow on yaw and leave the centres
 * almost where they were, so the long hulls sweep through each other as they
 * spin. This pushes the centres apart by the overlap plus a skin, keeps a share
 * of the closing speed as separation, and caps the yaw so each bow sweeps no
 * further than half of the gap that separation opens.
 */
export function separateAfterImpact(
  a: ImpactBody,
  b: ImpactBody,
  normal: Vec2,
  depth: number,
  changeA: ImpactChange,
  changeB: ImpactChange,
  remainingTurn: number,
  closingSpeed: number,
  harshness: number = CRASH_HARSHNESS,
): { a: PartedHull; b: PartedHull } {
  const mass = a.mass + b.mass;
  const push = Math.max(0, depth) + BOAT_SKIN_PX;
  const centerA = add(a.capsule.center, scale(normal, (-push * b.mass) / mass));
  const centerB = add(b.capsule.center, scale(normal, (push * a.mass) / mass));
  const span = Math.max(0, remainingTurn);
  const floor = Math.max(0, closingSpeed) * bounceKeep(harshness);
  const relN = dot(sub(changeB.velocity, changeA.velocity), normal);
  const boost = Math.max(0, floor - relN);
  const velocityA = add(changeA.velocity, scale(normal, (-boost * b.mass) / mass));
  const velocityB = add(changeB.velocity, scale(normal, (boost * a.mass) / mass));
  const open = Math.max(0, dot(sub(velocityB, velocityA), normal)) * span + BOAT_SKIN_PX;

  return {
    a: {
      center: centerA,
      velocity: velocityA,
      yaw: capYaw(changeA.yaw, open, hullReach(a.capsule)),
    },
    b: {
      center: centerB,
      velocity: velocityB,
      yaw: capYaw(changeB.yaw, open, hullReach(b.capsule)),
    },
  };
}

/** Share of the closing speed that has to come back out along the normal. */
function bounceKeep(harshness: number): number {
  return Math.max(0, 1 - Math.max(0, harshness) * (1 - BOAT_BOUNCE_KEEP));
}

/** Half the opening, shared by the two bows, so neither can swing across the gap. */
function capYaw(yaw: number, open: number, reach: number): number {
  const limit = open / (2 * Math.max(reach, 1));

  return clamp(yaw, -limit, limit);
}

/**
 * Effective restitution. Harshness is the same knob the shore uses: 1 is the
 * authored crumple curve, 0 keeps the whole closing speed (a perfectly elastic
 * nudge), 2 doubles the designed loss and floors at a dead stop.
 */
function restitution(closing: number, harshness: number): number {
  const crumpled = restitutionAt(Math.abs(closing));

  return Math.max(0, 1 - (1 - crumpled) * Math.max(0, harshness));
}

/** Inverse effective mass along `axis`: how much both hulls give when pushed there. */
function shareAlong(
  a: ImpactBody,
  b: ImpactBody,
  leverA: Vec2,
  leverB: Vec2,
  axis: Vec2,
): number {
  const twistA = cross(leverA, axis);
  const twistB = cross(leverB, axis);

  return (
    1 / a.mass +
    1 / b.mass +
    (twistA * twistA) / a.inertia +
    (twistB * twistB) / b.inertia
  );
}

function change(
  body: ImpactBody,
  lever: Vec2,
  applied: Vec2,
  remainingTurn: number,
): ImpactChange {
  return {
    velocity: add(body.velocity, scale(applied, 1 / body.mass)),
    yaw: (cross(lever, applied) / body.inertia) * remainingTurn,
  };
}

/** 2D scalar cross product; the z of the 3D one. */
function cross(a: Vec2, b: Vec2): number {
  return a.x * b.y - a.y * b.x;
}

function clampMagnitude(value: number, limit: number): number {
  const ceiling = Math.abs(limit);

  return Math.max(-ceiling, Math.min(ceiling, value));
}

/** True when the pair could not possibly reach each other. Cheap pre-test for the sweep. */
export function hullsWithinReach(a: HullCapsule, b: HullCapsule, slack = 0): boolean {
  const gap = hullReach(a) + hullReach(b) + slack;

  return lengthSq(sub(b.center, a.center)) <= gap * gap;
}
